import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
} from '@mui/material';
import { intentionallyFloatingPromiseReturn } from 'core/utils/commonUtils';
import { SWR_PRODUCTS_ENDPOINT } from 'hooks/swr/useProducts';
import useResponsive from 'hooks/useResponsive';
import { isArray, isNil } from 'lodash';
import { useConfirm } from 'material-ui-confirm';
import { useRef, useState } from 'react';
import * as productsService from 'services/productService';
import { mutate } from 'swr';
import ProductEdit from './ProductEdit';
import type { FC } from 'react';

const ProductDialog: FC<ProductDialogProps> = ({ open, productId, groupId, onClose }) => {
  const fullScreen = useResponsive('down', 'sm');
  const productEditRef = useRef<{ save: () => void } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const deletionConfirm = useConfirm();

  const deleteProduct = async (): Promise<void> => {
    if (!isNil(productId)) {
      await deletionConfirm({ description: 'Удалить товар?' })
        .then(async () => {
          setIsDeleting(true);

          await productsService.deleteProductAsync([productId]);

          return true;
        })
        .then(async () => {
          setIsDeleting(false);

          onClose();

          return await mutate(
            (key) => isArray(key) && key[0] === SWR_PRODUCTS_ENDPOINT,
            undefined
          );
        })
        .catch((err) => {
          setIsDeleting(false);
          return err;
        });
    }
  };

  const onSaveHandle = (loading: boolean, error: boolean): void => {
    // TODO: исправить костыль
    if (!loading && !error) {
      onClose();
    }

    setIsSaving(loading);
  };

  const onSaveClickHandle: VoidFunction = () => {
    productEditRef.current?.save();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={'md'}
      fullWidth
      fullScreen={fullScreen}>
      <DialogTitle>Товар</DialogTitle>
      <DialogContent>
        <ProductEdit
          ref={productEditRef}
          productId={productId}
          groupId={groupId}
          onSave={onSaveHandle}
        />
      </DialogContent>
      <DialogActions>
        <Box component={'div'} flex={1}>
          {!isNil(productId) && (
            <>
              {isDeleting ? (
                <CircularProgress size={24} />
              ) : (
                <Tooltip title='Удалить'>
                  <IconButton
                    aria-label='Удалить товар'
                    color='warning'
                    onClick={intentionallyFloatingPromiseReturn(deleteProduct)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
        <LoadingButton loading={isSaving} onClick={onSaveClickHandle}>
          <span>Сохранить</span>
        </LoadingButton>
        <Button onClick={onClose}>Отмена</Button>
      </DialogActions>
    </Dialog>
  );
};

export interface ProductDialogProps {
  open: boolean;
  productId?: number;
  groupId?: number;
  onClose: () => void;
}

export default ProductDialog;
